import './index.css'; // Ensure CSS is imported

// DO NOT REMOVE. This side-effect import looks unused; it is load-bearing.
//
// React resolves to CommonJS, so the bundler wraps it in a `__commonJS` interop
// helper and places that helper in whichever chunk is the common ancestor. Once
// `./app` below became a DYNAMIC import, that ancestor was the `app` chunk while
// React was split into its own `jsx-runtime` chunk — which then had to import the
// helper back out of `app`, while `app` imports `jsx-runtime`. A cycle.
//
// ES modules evaluate depth-first, so `jsx-runtime` ran before `app` had
// initialised the helper: `TypeError: C is not a function`, React never defined
// itself, nothing rendered. Combined with the swallowed rejection below that
// presented as a permanently stuck splash screen, empty console, 200 on every
// asset. It took production down on 2026-08-18.
//
// Importing the runtime HERE, from the entry, makes it statically reachable from
// the entry chunk, so the helper lands there and nothing imports back into it.
// The cycle cannot form. Verified by loading the built bundle in a real browser,
// not by reading the graph.
import 'react/jsx-runtime';

// Boot shim. Deliberately imports NOTHING from the app: its only job is to run
// before the app's module graph is evaluated, so anything set up here (local
// mocks) is in place first. The app itself lives in `./app` and is pulled in
// dynamically below.

// Diagnostic: when the "Something went wrong!" boundary fires from a React
// element-type error (#130) we lose the responsible component in production
// minification. Log the full error + componentStack to the console so a single
// reproduction in DevTools points straight at the offending JSX.
window.addEventListener('error', (event) => {
  if (event.error && /Minified React error #?\d+/i.test(event.message ?? '')) {
    // eslint-disable-next-line no-console
    console.error('[react-error]', event.error, '\nstack:', event.error?.stack);
  }
});

/**
 * Loads local mock fixtures when they exist, for localhost testing.
 *
 * `src/mocks/` is gitignored — it is a developer's own scratch data, never
 * part of a build. This hook is the committed half: `import.meta.glob`
 * resolves at build time and yields `{}` when the folder is absent, so a
 * checkout without mocks compiles and behaves exactly as before, while a
 * developer who drops in `src/mocks/install.ts` gets it picked up with no edit
 * to this file (and so no mock wiring can be committed by accident).
 *
 * Awaited before `./app` is imported, not merely before the first render: a
 * `fetch` patch has to be installed before any module can capture the original
 * (Better Auth's client stores `globalThis.fetch` as `customFetchImpl` the
 * moment `createAuthClient()` is evaluated, and a later patch never reaches
 * it — the mock session silently fell through to the real backend).
 */
async function loadLocalMocks(): Promise<void> {
  if (!import.meta.env.DEV) return;
  try {
    const found = import.meta.glob('./mocks/install.ts');
    const load = found['./mocks/install.ts'];
    if (load) await load();
  } catch (error) {
    // A broken fixture must not stop the app from booting.
    // eslint-disable-next-line no-console
    console.error('[mocks] local fixtures failed to load:', error);
  }
}

// A rejection anywhere in this chain used to be swallowed: `void` discards the
// promise, and with no catch the splash in index.html is never removed, so ANY
// throw while evaluating the app's module graph presents as a permanently
// stuck loading screen with nothing in the console and a 200 on every asset.
// That is exactly how it failed in production on 2026-08-18. Surface it, and
// replace the splash with something that says so, rather than spinning forever.
void loadLocalMocks()
  .then(() => import('./app'))
  .then(({ mount }) => mount())
  .catch((error) => {
    // eslint-disable-next-line no-console
    console.error('[boot] the app failed to start:', error);
    const splash = document.getElementById('doji-splash');
    if (splash) {
      splash.classList.add('doji-splash-out');
      window.setTimeout(() => splash.remove(), 300);
    }
    const root = document.getElementById('root');
    if (root && !root.innerHTML) {
      root.innerHTML =
        '<div style="min-height:100vh;display:flex;align-items:center;justify-content:center;' +
        'flex-direction:column;gap:8px;background:#0a0a0a;color:#e5e5e5;font-family:ui-monospace,monospace;' +
        'text-align:center;padding:24px;">' +
        '<div style="font-size:15px;">This page failed to load.</div>' +
        '<div style="font-size:13px;opacity:.6;">Please refresh. If it persists, the console has the error.</div>' +
        '</div>';
    }
  });
